import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { TableOfContents } from '@/components/content/TableOfContents';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';

const ARTICLE_DESCRIPTION =
  "An athlete's guide to setting up a Sport Fund page, telling your story, and getting your first contributions from family, friends, club and sponsors.";

const FAQS = [
  {
    q: 'How long does it take to set up a Sport Fund page?',
    a: 'The setup checklist on your dashboard walks you through it in under fifteen minutes: profile photo, cover image, bio, sport, level, and connecting your bank account through Stripe so contributions go directly to you.',
  },
  {
    q: 'What should I include before sharing my page?',
    a: "Post at least one update before you share widely — a training video, a photo from your last competition, or a paragraph about your next block of training. Pages with at least one update convert dramatically better than empty ones.",
  },
  {
    q: 'Should I share my page publicly right away?',
    a: "No — launch to your warmest circle first. Send the ten or fifteen people closest to you a short personal message with what you're chasing this season, then expand to your wider network once you have a handful of contributions.",
  },
  {
    q: 'Does a funding breakdown actually help?',
    a: 'Yes. Adding two or three concrete line items — like coaching, equipment, or competition travel with real costs — is the single biggest difference between a page that gets backed and one that gets ignored.',
  },
  {
    q: 'Does Sport Fund hold or take a cut of my money?',
    a: "No. Sport Fund never holds your money — contributions go directly to your bank account through Stripe, and athletes pay nothing to use the platform.",
  },
];

const GivingWithPurpose: React.FC = () => {
  usePageMeta('Funding Your Sport', ARTICLE_DESCRIPTION, {
    article: true,
    image: '/resources/athlete-training.jpg',
    breadcrumbs: [{ name: 'Resources', path: '/resources' }, { name: 'Funding Your Sport' }],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  });
  const TOC_SECTIONS = [
    {
      id: "1-get-your-story-straight-first",
      text: "1. Get your story straight first"
    },
    {
      id: "2-set-up-your-page-in-under-fifteen-minutes",
      text: "2. Set up your page in under fifteen minutes"
    },
    {
      id: "3-add-a-funding-breakdown",
      text: "3. Add a funding breakdown"
    },
    {
      id: "4-launch-to-your-warmest-circle-first",
      text: "4. Launch to your warmest circle first"
    },
    {
      id: "5-post-your-first-update-before-you-share-widely",
      text: "5. Post your first update before you share widely"
    },
    {
      id: "6-then-expand-the-circle",
      text: "6. Then expand the circle"
    },
    {
      id: "frequently-asked-questions",
      text: "Frequently asked questions"
    },
    {
      id: "next-keep-the-people-in-the-loop",
      text: "Next: keep the people in the loop"
    }
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link to="/resources" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Resources
      </Link>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">Funding Your Sport: An Athlete's Guide to Getting Started</h1>
      <p className="text-gray-500 mb-8 text-lg">Everything you need to set up your fund page, tell your story, and reach the people who want to back you.</p>

      <article className="prose prose-lg max-w-none">
        <TableOfContents sections={TOC_SECTIONS} />
        <p>
          Competing isn't free. Whether you're a junior chasing a national selection, a club athlete preparing for a European tour, or a master's competitor paying entry fees out of pocket — the cost of equipment, travel, coaching and federation fees adds up fast. Sport Fund exists so the people who already believe in you have an easy, dignified way to be part of the journey.
        </p>
        <p>
          This guide walks you through everything you need to launch a fund page that actually works.
        </p>

        <h2 id="1-get-your-story-straight-first">1. Get your story straight first</h2>
        <p>
          Before you touch the platform, take fifteen minutes to write three things on paper:
        </p>
        <ul>
          <li><strong>What sport you compete in</strong>, your discipline within that sport, and your current level (regional, national, international, masters, junior, etc.).</li>
          <li><strong>The specific goal you're chasing this season</strong> — a target competition, a qualifying time, a ranking, a coaching block.</li>
          <li><strong>What the money will actually be spent on</strong>. Be concrete: "I need €3,200 to cover four European Cup entries, flights and hotels," not "I need money for my sport." Supporters give more when they know what they're funding.</li>
        </ul>

        <h2 id="2-set-up-your-page-in-under-fifteen-minutes">2. Set up your page in under fifteen minutes</h2>
        <p>
          The setup checklist on your dashboard walks you through it: profile photo, cover image, bio, sport, level, and connecting your bank account through Stripe so contributions go directly to you. Sport Fund never holds your money.
        </p>
        <p>A few things that move the needle:</p>
        <ul>
          <li>A clear, well-lit photo of you in your kit or at training. Phone-quality is fine.</li>
          <li>A cover image from competition or training — action shots work better than headshots here.</li>
          <li>A short bio (under 100 words) that anyone outside your sport could read and understand.</li>
        </ul>

        <h2 id="3-add-a-funding-breakdown">3. Add a funding breakdown</h2>
        <p>
          A funding breakdown — line items showing what costs what — is the single biggest difference between a page that gets backed and one that gets ignored. Examples:
        </p>
        <ul>
          <li>Equipment replacement (e.g. running shoes every 600 km) — $90/month</li>
          <li>Coaching — $200/month</li>
          <li>Federation licence + insurance — $180/year</li>
          <li>Competition entries + travel — $400/event × 6 events</li>
        </ul>
        <p>
          You don't have to share every line. But adding two or three concrete items makes "support an athlete" feel like "buy a pair of training shoes for someone I know" — which is much easier to say yes to.
        </p>

        <h2 id="4-launch-to-your-warmest-circle-first">4. Launch to your warmest circle first</h2>
        <p>
          Don't post the link publicly on day one. Start with the ten or fifteen people who would be most disappointed if they heard about your funding goal second-hand: immediate family, your coach, training partners, your club president. A few first contributions from people in your inner circle build momentum and social proof.
        </p>
        <p>
          Send each of them a short personal message — not a copy-paste. Tell them what you're chasing this season and exactly what their contribution will go toward. Then share the link.
        </p>

        <h2 id="5-post-your-first-update-before-you-share-widely">5. Post your first update before you share widely</h2>
        <p>
          Before you blast the page to the wider world, post at least one update on the page itself: a training video, a photo from your last competition, a paragraph about the next block of training. Pages with at least one update convert dramatically better than empty ones.
        </p>

        <h2 id="6-then-expand-the-circle">6. Then expand the circle</h2>
        <p>
          Once you have a handful of contributions and an update or two, share with your full network: your club's WhatsApp group, your social channels, your school or workplace, sponsors you've worked with before, alumni networks of clubs and federations.
        </p>
        <p>
          The page is yours forever. You can keep it live through the whole season and across seasons. The hardest part is launching — once it's live, the only thing that matters is consistent updates.
        </p>

        <h2 id="frequently-asked-questions">Frequently asked questions</h2>
        <div className="not-prose space-y-6">
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">{f.q}</h3>
              <p className="text-gray-600">{f.a}</p>
            </div>
          ))}
        </div>

        <h2 id="next-keep-the-people-in-the-loop">Next: keep the people in the loop</h2>
        <p>
          See <Link to="/resources/engaging-your-supporters" className="text-blue-600">Engaging Your Supporters</Link> for a practical framework for posting updates that keep family, friends and club connected to your journey.
        </p>
        <ArticleFeedback articleSlug="funding-your-education" />
      </article>
    </div>
  );
};

export default GivingWithPurpose;
